import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    NODE_ENV: z.enum(["development", "test", "production"]),
    APPSIGNAL_ACTIVE: z.enum(["true", "false"]).default("false"),
    APPSIGNAL_PUSH_API_KEY: z.string().optional(),
    APPSIGNAL_APP_NAME: z.string().default("path-collective-web"),
    APPSIGNAL_APP_ENV: z.string().optional(),
    APPSIGNAL_REVISION: z.string().optional(),
    APPSIGNAL_DEBUG_ROUTES_ENABLED: z.enum(["true", "false"]).default("false"),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_API_URL: z.string(),
    NEXT_PUBLIC_SUPABASE_URL: z.string(),
    NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string(),
    NEXT_PUBLIC_IMAGE_BASE_URL: z.string(),
    // Public origin of the uploads bucket (DO Spaces host or a CDN). Optional:
    // unset falls back to the URL derived from the presigned PUT.
    NEXT_PUBLIC_UPLOADS_BASE_URL: z.string().optional(),
    NEXT_PUBLIC_MAPBOX_TOKEN: z.string(),
    // NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is deliberately absent. Checkout is
    // Stripe-HOSTED — the app POSTs for a session and redirects to Stripe, so
    // no publishable key is ever needed in the browser. It was declared here
    // for a client-side <PaymentForm> that nothing rendered, and because it
    // was never wired through the Dockerfile as a build ARG it could not have
    // reached the bundle anyway. Declaring it invited the recurring question
    // of why a "configured" key was missing in production (#525).
    //
    // If client-side Stripe is ever genuinely needed: re-add it here AND add
    // matching ARG/ENV lines to BOTH stages of apps/web/Dockerfile and the
    // build args in .github/workflows/web.yml — NEXT_PUBLIC_* is inlined at
    // BUILD time, so supplying it only at runtime does nothing. The AppSignal
    // front-end vars are the worked example.
    NEXT_PUBLIC_WEB_URL: z.string().url().optional(),
    // AppSignal front-end error monitoring. This is the FRONT-END key from
    // the app's "Push & Deploy" settings, NOT APPSIGNAL_PUSH_API_KEY — the
    // two are different credentials. Optional: with no key the browser SDK
    // stays in development mode and client errors fall back to the
    // /api/exceptions server forwarder.
    NEXT_PUBLIC_APPSIGNAL_FRONTEND_KEY: z.string().optional(),
    NEXT_PUBLIC_APPSIGNAL_APP_ENV: z.string().optional(),
    NEXT_PUBLIC_APPSIGNAL_REVISION: z.string().optional(),
    // #554 — comma-separated allowlist of social sign-in buttons to render
    // (e.g. "google,apple"). Unset ⇒ Google only. See src/lib/sso-providers.ts.
    NEXT_PUBLIC_ENABLED_SSO_PROVIDERS: z.string().optional(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,
    APPSIGNAL_ACTIVE: process.env.APPSIGNAL_ACTIVE,
    APPSIGNAL_PUSH_API_KEY: process.env.APPSIGNAL_PUSH_API_KEY,
    APPSIGNAL_APP_NAME: process.env.APPSIGNAL_APP_NAME,
    APPSIGNAL_APP_ENV: process.env.APPSIGNAL_APP_ENV,
    APPSIGNAL_REVISION: process.env.APPSIGNAL_REVISION,
    APPSIGNAL_DEBUG_ROUTES_ENABLED: process.env.APPSIGNAL_DEBUG_ROUTES_ENABLED,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_IMAGE_BASE_URL: process.env.NEXT_PUBLIC_IMAGE_BASE_URL,
    NEXT_PUBLIC_UPLOADS_BASE_URL: process.env.NEXT_PUBLIC_UPLOADS_BASE_URL,
    NEXT_PUBLIC_MAPBOX_TOKEN: process.env.NEXT_PUBLIC_MAPBOX_TOKEN,
    NEXT_PUBLIC_WEB_URL: process.env.NEXT_PUBLIC_WEB_URL,
    NEXT_PUBLIC_APPSIGNAL_FRONTEND_KEY: process.env.NEXT_PUBLIC_APPSIGNAL_FRONTEND_KEY,
    NEXT_PUBLIC_APPSIGNAL_APP_ENV: process.env.NEXT_PUBLIC_APPSIGNAL_APP_ENV,
    NEXT_PUBLIC_APPSIGNAL_REVISION: process.env.NEXT_PUBLIC_APPSIGNAL_REVISION,
    NEXT_PUBLIC_ENABLED_SSO_PROVIDERS: process.env.NEXT_PUBLIC_ENABLED_SSO_PROVIDERS,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
